import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Trash2, Palette, Upload, Link, RectangleVertical, RectangleHorizontal, Square } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = ['sports', 'school', 'general', 'abstract'];

const BackgroundsPage = () => {
  const [backgrounds, setBackgrounds] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, bg: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, bg: null });
  const [uploading, setUploading] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('file');
  const [customCategory, setCustomCategory] = useState('');
  const [orientationFilter, setOrientationFilter] = useState('all');
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    name: '',
    url: '',
    category: ''
  });
  
  const [fileForm, setFileForm] = useState({
    name: '',
    category: '',
    file: null
  });

  useEffect(() => {
    fetchBackgrounds();
    fetchCategories();
  }, []);

  const fetchBackgrounds = async () => {
    try {
      const response = await axios.get(`${API}/backgrounds`);
      setBackgrounds(response.data);
    } catch (error) {
      toast.error('Failed to load backgrounds');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/backgrounds/categories`);
      const existingCats = response.data || [];
      // Merge existing categories with defaults
      const allCats = [...new Set([...DEFAULT_CATEGORIES, ...existingCats])];
      setCategories(allCats.sort());
    } catch (error) {
      // Keep default categories if fetch fails
    }
  };

  const handleAddCustomCategory = () => {
    if (customCategory.trim() && !categories.includes(customCategory.toLowerCase())) {
      setCategories(prev => [...prev, customCategory.toLowerCase()].sort());
      setCustomCategory('');
    }
  };

  const resetForm = () => {
    setForm({ name: '', url: '', category: '' });
    setFileForm({ name: '', category: '', file: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.url || !form.category) {
      toast.error('Please fill in all fields');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/backgrounds`, {
        name: form.name,
        url: form.url,
        thumbnail_url: form.url,
        category: form.category
      });
      toast.success('Background added');
      setShowDialog(false);
      resetForm();
      fetchBackgrounds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add background');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    
    if (!fileForm.name || !fileForm.category || !fileForm.file) {
      toast.error('Please fill in all fields and select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', fileForm.name);
      formData.append('category', fileForm.category);
      formData.append('file', fileForm.file);

      await axios.post(`${API}/upload/background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Background uploaded');
      setShowDialog(false);
      resetForm();
      fetchBackgrounds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload background');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileForm({ ...fileForm, file });
      // Auto-fill name from filename if empty
      if (!fileForm.name) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        setFileForm(prev => ({ ...prev, file, name: nameWithoutExt }));
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.bg) return;
    try {
      await axios.delete(`${API}/backgrounds/${deleteDialog.bg.id}`);
      toast.success('Background deleted');
      setDeleteDialog({ open: false, bg: null });
      fetchBackgrounds();
    } catch (error) {
      toast.error('Failed to delete background');
    }
  };

  const groupedBackgrounds = backgrounds.reduce((acc, bg) => {
    if (!acc[bg.category]) acc[bg.category] = [];
    acc[bg.category].push(bg);
    return acc;
  }, {});

  // Filter by orientation
  const filteredBackgrounds = orientationFilter === 'all' 
    ? backgrounds 
    : backgrounds.filter(bg => bg.orientation === orientationFilter);

  const filteredGroupedBackgrounds = filteredBackgrounds.reduce((acc, bg) => {
    if (!acc[bg.category]) acc[bg.category] = [];
    acc[bg.category].push(bg);
    return acc;
  }, {});

  const getOrientationIcon = (orientation) => {
    switch (orientation) {
      case 'portrait': return <RectangleVertical className="h-3 w-3" />;
      case 'landscape': return <RectangleHorizontal className="h-3 w-3" />;
      default: return <Square className="h-3 w-3" />;
    }
  };

  const getOrientationColor = (orientation) => {
    switch (orientation) {
      case 'portrait': return 'bg-blue-100 text-blue-700';
      case 'landscape': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading text-stone-800">Backgrounds</h1>
          <p className="text-stone-600 mt-1">Manage digital backgrounds for photo products</p>
        </div>
        <Button 
          className="btn-action" 
          onClick={() => { resetForm(); setShowDialog(true); }}
          data-testid="new-background-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Background
        </Button>
      </div>

      {/* Orientation Filter */}
      <div className="flex items-center gap-4">
        <Label className="text-sm text-slate-600">Filter by orientation:</Label>
        <div className="flex gap-2">
          <Button 
            variant={orientationFilter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setOrientationFilter('all')}
          >
            All ({backgrounds.length})
          </Button>
          <Button 
            variant={orientationFilter === 'portrait' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setOrientationFilter('portrait')}
            className={orientationFilter === 'portrait' ? 'bg-blue-600' : ''}
          >
            <RectangleVertical className="h-4 w-4 mr-1" />
            Portrait ({backgrounds.filter(b => b.orientation === 'portrait').length})
          </Button>
          <Button 
            variant={orientationFilter === 'landscape' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setOrientationFilter('landscape')}
            className={orientationFilter === 'landscape' ? 'bg-green-600' : ''}
          >
            <RectangleHorizontal className="h-4 w-4 mr-1" />
            Landscape ({backgrounds.filter(b => b.orientation === 'landscape').length})
          </Button>
          <Button 
            variant={orientationFilter === 'square' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setOrientationFilter('square')}
          >
            <Square className="h-4 w-4 mr-1" />
            Square ({backgrounds.filter(b => b.orientation === 'square').length})
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-48 bg-stone-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : filteredBackgrounds.length > 0 ? (
        <div className="space-y-8">
          {Object.entries(filteredGroupedBackgrounds).map(([category, bgs]) => (
            <div key={category}>
              <h2 className="font-heading text-xl text-stone-800 mb-4 capitalize">
                {category}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {bgs.map((bg) => (
                  <div key={bg.id} className="group relative">
                    <div className={`rounded-sm overflow-hidden border border-stone-200 ${
                      bg.orientation === 'portrait' ? 'aspect-[3/4]' : 
                      bg.orientation === 'landscape' ? 'aspect-[4/3]' : 'aspect-square'
                    }`}>
                      <img
                        src={bg.thumbnail_url || bg.url}
                        alt={bg.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    {/* Orientation badge */}
                    <div className="absolute top-2 left-2">
                      <Badge className={`text-xs ${getOrientationColor(bg.orientation)}`}>
                        {getOrientationIcon(bg.orientation)}
                        <span className="ml-1 capitalize">{bg.orientation || 'unknown'}</span>
                      </Badge>
                    </div>
                    <div className="absolute inset-0 bg-stone-900/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm flex items-center justify-center">
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setDeleteDialog({ open: true, bg })}
                        data-testid={`delete-bg-${bg.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-stone-700 mt-2 truncate">{bg.name}</p>
                    {bg.width && bg.height && (
                      <p className="text-xs text-stone-400">{bg.width} x {bg.height}px</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : backgrounds.length > 0 ? (
        <Card className="border-stone-200">
          <CardContent className="py-16 text-center">
            <Palette className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-stone-800 mb-2">No {orientationFilter} backgrounds</h3>
            <p className="text-stone-500 mb-6">Try a different orientation filter or add new backgrounds</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200">
          <CardContent className="py-16 text-center">
            <Palette className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-stone-800 mb-2">No backgrounds yet</h3>
            <p className="text-stone-500 mb-6">Add digital backgrounds for clients to choose from</p>
            <Button 
              className="btn-action"
              onClick={() => { resetForm(); setShowDialog(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Background
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Background Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Background</DialogTitle>
            <DialogDescription>
              Upload from your computer or add from a URL
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={uploadMethod} onValueChange={setUploadMethod} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="file" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload File
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link className="h-4 w-4" />
                From URL
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="file">
              <form onSubmit={handleFileSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="file">Choose Image</Label>
                  <Input
                    id="file"
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    data-testid="bg-file-input"
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-stone-500">Supports: JPEG, PNG, WebP</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file-name">Background Name *</Label>
                  <Input
                    id="file-name"
                    placeholder="e.g., Blue Sports Field"
                    value={fileForm.name}
                    onChange={(e) => setFileForm({ ...fileForm, name: e.target.value })}
                    data-testid="bg-file-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={fileForm.category} onValueChange={(v) => setFileForm({ ...fileForm, category: v })}>
                    <SelectTrigger data-testid="bg-file-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Or add new category..."
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      className="text-sm"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={handleAddCustomCategory}>
                      Add
                    </Button>
                  </div>
                </div>

                {fileForm.file && (
                  <div className="space-y-2">
                    <Label>Preview</Label>
                    <div className="aspect-[4/3] rounded-sm overflow-hidden border border-stone-200 bg-stone-100">
                      <img
                        src={URL.createObjectURL(fileForm.file)}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-action" disabled={uploading} data-testid="save-bg-file-btn">
                    {uploading ? 'Uploading...' : 'Upload Background'}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
            
            <TabsContent value="url">
              <form onSubmit={handleUrlSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="url-name">Background Name *</Label>
                  <Input
                    id="url-name"
                    placeholder="e.g., Blue Sports Field"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="bg-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="bg-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Or add new category..."
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      className="text-sm"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={handleAddCustomCategory}>
                      Add
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="url">Image URL *</Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://example.com/background.jpg"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    data-testid="bg-url-input"
                  />
                </div>

                {form.url && (
                  <div className="space-y-2">
                    <Label>Preview</Label>
                    <div className="aspect-[4/3] rounded-sm overflow-hidden border border-stone-200 bg-stone-100">
                      <img
                        src={form.url}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-action" disabled={uploading} data-testid="save-bg-btn">
                    {uploading ? 'Adding...' : 'Add Background'}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, bg: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Background</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.bg?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, bg: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-bg-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackgroundsPage;
